<script lang="ts">
  import { updateLine, removeLines } from '$lib/stores/Cart.svelte';
  import { productMappings } from '$lib/stores/stores.svelte';
  import type { CartFieldsFragment } from '$lib/types/storefront.generated';
  import { Minus, Plus, Trash } from "@lucide/svelte";
  import { m } from "$lib/paraglide/messages";
  import { formatPrice, formatTitle, slugify } from '$lib/utils/shopify';
  import { localizeHref } from "$lib/paraglide/runtime";

  type CartLineNode = CartFieldsFragment['lines']['edges'][number]['node'];

  let {node, compact = false}: {node: CartLineNode, compact?: boolean} = $props();

  const variant   = node.merchandise;          // ProductVariant
  const product   = variant.product;
  const price     = parseFloat(variant.price.amount);
  const imageUrl  = variant.image?.url;
  const altText   = variant.image?.altText ?? product.title;

  // Ships immediately unless the ERP sync marked the variant "weeks"; no tier never means 2-3 weeks
  const isInStore = $derived(variant.availabilityTier?.value !== 'weeks');

  // Use global mappings for consistent slug/title, fall back to computing from title
  const productSlug = $derived(productMappings.slugMapping[product.handle] ?? slugify(product.title));
  const productTitle = $derived(productMappings.titleMapping[product.handle] ?? formatTitle(product.title));

  // Extract just the size from variant.title (format: "42 / Black" -> "42")
  // Skip color since it's already in the product name
  const variantSize = $derived.by(() => {
    const title = variant.title;
    if (!title) return '';
    // Split by " / " and take first part (size), skip color
    const parts = title.split(' / ');
    // Return first part (size) - if there's only one part, it's the whole title
    return parts[0] || title;
  });

  function inc()  {
    waitingOnServer = true;
    updateLine(node.id, node.quantity + 1);
    waitingOnServer = false;
  }
  function dec()  {
    waitingOnServer = true;
    if (node.quantity > 1) updateLine(node.id, node.quantity - 1);
    else removeLines([node.id]);
    waitingOnServer = false;
  }
  function remove() {
    waitingOnServer = true;
    removeLines([node.id]);
    waitingOnServer = false;
  }

  let waitingOnServer = $state(false);
</script>



{#if compact}
<!-- Compact layout for popup -->
<div class="flex items-center gap-3 py-3">
  {#if imageUrl}
  <a href={localizeHref(`/shoes/${productSlug}/`)} class="flex-shrink-0 w-16 h-16 rounded overflow-hidden bg-white">
    <img src={imageUrl} alt="{altText}" class="w-full h-full object-contain" />
  </a>
  {/if}

  <div class="flex-1 min-w-0">
    <a href={localizeHref(`/shoes/${productSlug}/`)} class="block">
      <h3 class="text-sm font-medium text-gray-900 dark:text-white truncate hover:underline">
        {productTitle}
      </h3>
    </a>
    <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{m.size()} <span dir="ltr">{variantSize}</span></p>
    <p class="text-sm font-medium text-gray-900 dark:text-white mt-0.5">€ {price.toFixed(2)}</p>
    <p class="text-[10px] italic text-gray-400 dark:text-gray-500 truncate">
      {#if isInStore}
        {m.availability_in_store()}
      {:else}
        {m.availability_order()}
      {/if}
    </p>
  </div>

  <div class="flex items-center gap-1 flex-shrink-0">
    <button
      type="button"
      aria-label={node.quantity > 1 ? "Decrease quantity" : "Remove"}
      class="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      disabled={waitingOnServer}
      onclick={(e) => { e.preventDefault(); node.quantity > 1 ? dec() : remove(); }}
    >
      {#if node.quantity > 1}
        <Minus size={16} />
      {:else}
        <Trash size={16} />
      {/if}
    </button>
    <span class="w-6 text-center text-sm text-gray-900 dark:text-white">{node.quantity}</span>
    <button
      type="button"
      aria-label="Increase quantity"
      class="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
      disabled={waitingOnServer}
      onclick={(e) => { e.preventDefault(); inc(); }}
    >
      <Plus size={16} />
    </button>
  </div>
</div>
{:else}
<!-- Full layout for cart page -->
<a class="flex py-6 border-t border-gray-200 dark:border-gray-700" href={localizeHref(`/shoes/${productSlug}/`)}>
    <!-- Image -->
     {#if imageUrl}
    <div class="flex-shrink-0 w-32 md:w-44 lg:w-64 rounded-md overflow-hidden">
      <img src={imageUrl} alt="{altText}" class="w-full h-full object-contain" />
    </div>
    {/if}

    <div class="ms-4 flex-1 flex flex-col justify-between sm:ms-6">
      <!-- Top Section: Details & Price -->
      <div class="relative pe-9">
        <div>
          <h2 class="text-base font-medium text-gray-900 dark:text-white">
            <div class="hover:underline"> {productTitle} </div>
          </h2>
          <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {m.size()} <span class="font-medium text-gray-900 dark:text-gray-100" dir="ltr">{variantSize}</span>
          </p>
          <!-- Availability notice -->
          <p class="mt-1 text-xs italic text-gray-400 dark:text-gray-500">
            {#if isInStore}
              {m.availability_in_store()}
            {:else}
              {m.availability_order()} — {m.availability_order_notice()}
            {/if}
          </p>
        </div>
        <div class="mt-1 absolute top-0 end-0">
          <p class="text-base font-medium text-gray-900 dark:text-white">€ {price.toFixed(2)}</p>
        </div>
      </div>

      <!-- Middle Section: Actions -->
       <div class="flex items-center justify-between mt-4">
          <div class="flex items-center border border-gray-300 dark:border-gray-600 rounded-md">
            {#if node.quantity > 1}
            <button type="button" aria-label="Remove" class="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            disabled={waitingOnServer}
            onclick={()=>
                dec()
              }
            >
              <Minus></Minus>
            </button>
            {:else}
            <button type="button" aria-label="Remove" class="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            disabled={waitingOnServer}
            onclick={()=>
              remove()
            }>
                <Trash></Trash>
              </button>
            {/if}
            <span class="px-3 py-1 text-sm text-gray-900 dark:text-white">{node.quantity}</span>
            <button type="button" aria-label="Increment quantity" class="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
            disabled={waitingOnServer}
            onclick={()=>
              inc()
            }
            >
            <Plus></Plus>
            </button>
          </div>
       </div>
    </div>
  </a>
{/if}
